/**
 * iter248 — Category-driven Invoice creation wizard
 *
 * Opened from the Franchise Configuration → Invoice Requirements
 * dashboard by clicking [Generate Invoice] on any invoice category.
 * The relevant configured lines (from the frozen snapshot) are
 * pre-loaded — the user only reviews/edits and submits.  NEVER
 * re-asks the user to pick the category.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const currency = (v, code = 'INR') => {
  try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 2 }).format(v || 0); }
  catch { return `${code} ${(v || 0).toLocaleString()}`; }
};

const lineTotal = (l) => {
  const q = +(l.quantity || 0), r = +(l.rate || 0), d = +(l.discount || 0), g = +(l.gst_percent || 0);
  const taxable = Math.max(q * r - d, 0);
  return taxable + taxable * g / 100;
};

export function CategoryInvoiceWizard({ open, onClose, call, token, oid, onboarding, category, onCreated }) {
  const cur = onboarding?.currency_code || 'INR';
  const [reviewLines, setReviewLines] = useState([]);
  const [details, setDetails] = useState({
    due_date: '', remarks: '',
    invoice_issued_by: 'MFPL',
  });
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!category?.lines) return;
    setReviewLines(category.lines.map(l => ({
      id: l.id, description: l.description, item_code: l.item_code || '',
      unit: l.unit || 'NO', quantity: l.quantity || 1,
      rate: l.rate == null ? 0 : +l.rate,
      discount: l.discount || 0, gst_percent: l.gst_percent || 18,
      hsn_code: l.hsn_code || '',
      commercial_line_id: l.id,
    })));
  }, [category]);

  const updateLn = (i, k, v) => setReviewLines(a =>
    a.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmLn = (i) => setReviewLines(a => a.filter((_, idx) => idx !== i));

  const missingRateCount = reviewLines.filter(l => !l.rate || +l.rate <= 0).length;
  const grandTotal = reviewLines.reduce((s, l) => s + lineTotal(l), 0);

  const submit = async () => {
    if (missingRateCount > 0) return toast.error(`${missingRateCount} line(s) missing rate`);
    if (reviewLines.length === 0) return toast.error('Add at least one line');
    setSubmitting(true);
    try {
      const additional_lines = reviewLines.map(l => ({
        commercial_line_id: l.commercial_line_id || null,
        description: l.description, item_code: l.item_code || '',
        quantity: +l.quantity, unit: l.unit,
        rate: +l.rate, discount: +l.discount || 0,
        gst_percent: +l.gst_percent, hsn_code: l.hsn_code || '',
      }));
      const r = await call('/invoice/create', {
        onboarding_id: oid,
        invoice_type: category.invoice_category_code,
        invoice_category_code: category.invoice_category_code,
        invoice_issued_by: details.invoice_issued_by,
        due_date: details.due_date,
        remarks: details.remarks,
        line_ids: [], additional_lines,
      });
      toast.success(`Invoice ${r.invoice.invoice_number} created`);
      setPreview({ doc: r.invoice, lines: r.lines });
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const approve = async () => {
    if (!preview) return;
    try {
      await call('/invoice/approve', { invoice_id: preview.doc.id });
      toast.success('Invoice approved & locked');
      onCreated?.();
    } catch (e) { toast.error(e.message); }
  };

  const downloadPDF = async () => {
    if (!preview) return;
    try {
      const res = await fetch(`${API}/api/franchise-onboarding/invoice/pdf/${preview.doc.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `INV_${preview.doc.invoice_number.replace(/\//g, '-')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="cat-invoice-wizard">
        <DialogHeader>
          <DialogTitle>Generate Invoice — {category?.invoice_category_name || category?.invoice_category_code}</DialogTitle>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <div className="border border-blue-300 bg-blue-50 rounded p-3 text-xs" data-testid="cat-inv-banner">
              <div className="font-semibold text-blue-800 mb-1">📌 Invoice Category pre-selected from Franchise Configuration</div>
              <div>{reviewLines.length} configured line{reviewLines.length === 1 ? '' : 's'} pre-loaded from the frozen snapshot.</div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Issued By</Label>
                <Select value={details.invoice_issued_by} onValueChange={v => setDetails(d => ({ ...d, invoice_issued_by: v }))}>
                  <SelectTrigger data-testid="cat-inv-issued-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MFPL">MFPL</SelectItem>
                    <SelectItem value="VENDOR">VENDOR</SelectItem>
                    <SelectItem value="FRANCHISEE_DIRECT">FRANCHISEE_DIRECT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Due Date</Label><Input type="date" value={details.due_date} onChange={e => setDetails(d => ({ ...d, due_date: e.target.value }))} data-testid="cat-inv-due" /></div>
              <div><Label>Remarks</Label><Input value={details.remarks} onChange={e => setDetails(d => ({ ...d, remarks: e.target.value }))} data-testid="cat-inv-remarks" /></div>
            </div>

            <div className="border rounded max-h-[50vh] overflow-y-auto">
              <table className="w-full text-xs" data-testid="cat-inv-lines-table">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-left border-b">
                    <th className="p-1">#</th>
                    <th>Description</th>
                    <th className="text-center">Qty</th>
                    <th className="text-center">Unit</th>
                    <th className="text-right">Rate</th>
                    <th className="text-right">Discount</th>
                    <th className="text-center">GST%</th>
                    <th className="text-right">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reviewLines.map((l, i) => (
                    <tr key={i} className={`border-b ${!l.rate ? 'bg-amber-50' : ''}`}>
                      <td className="p-1">{i + 1}</td>
                      <td className="max-w-xs truncate">{l.description}</td>
                      <td><Input className="h-7 text-xs text-center w-16" type="number" value={l.quantity} onChange={e => updateLn(i, 'quantity', +e.target.value)} /></td>
                      <td className="text-center text-[10px]">{l.unit}</td>
                      <td><Input className="h-7 text-xs text-right w-24" type="number" value={l.rate || ''} onChange={e => updateLn(i, 'rate', +e.target.value)} placeholder={!l.rate ? 'Rate?' : ''} /></td>
                      <td><Input className="h-7 text-xs text-right w-20" type="number" value={l.discount} onChange={e => updateLn(i, 'discount', +e.target.value)} /></td>
                      <td><Input className="h-7 text-xs text-center w-14" type="number" value={l.gst_percent} onChange={e => updateLn(i, 'gst_percent', +e.target.value)} /></td>
                      <td className="text-right font-semibold">{currency(lineTotal(l), cur)}</td>
                      <td><Button size="sm" variant="ghost" onClick={() => rmLn(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button></td>
                    </tr>
                  ))}
                  {reviewLines.length === 0 && <tr><td colSpan={9} className="text-center py-4 text-muted-foreground">No lines configured under this category.</td></tr>}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/40 font-semibold text-xs">
                    <td colSpan={7} className="p-1 text-right">Grand Total</td>
                    <td className="text-right">{currency(grandTotal, cur)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {missingRateCount > 0 && (
              <div className="text-xs text-amber-700" data-testid="cat-inv-missing-rate">
                ⚠ {missingRateCount} line(s) missing rate — fill before submitting.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="border rounded p-3 bg-green-50 border-green-300">
              <div className="text-sm font-semibold text-green-800">Invoice created — {preview.doc.invoice_number}</div>
              <div className="text-xs">Status: DRAFT · Total: {currency(preview.doc.totals?.final_amount, cur)}</div>
            </div>
            <div className="flex gap-2">
              <Button onClick={downloadPDF} data-testid="cat-inv-download-pdf"><Download className="w-4 h-4 mr-1" />Download PDF</Button>
              <Button onClick={approve} className="bg-blue-600 hover:bg-blue-700" data-testid="cat-inv-approve">Approve & Lock</Button>
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3 mt-3">
          <Button variant="outline" onClick={onClose}>{preview ? 'Close' : 'Cancel'}</Button>
          {!preview && <Button onClick={submit} disabled={submitting || missingRateCount > 0} data-testid="cat-inv-submit">{submitting ? 'Saving…' : 'Create Invoice'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
